import { useMemo, useState } from "react";
import { resourceGroups, resourceItems } from "@/mocks/home";
import SectionHeading from "./SectionHeading";

export default function Resources() {
  const [activeGroup, setActiveGroup] = useState(resourceGroups[0].key);
  const [keyword, setKeyword] = useState("");

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return resourceItems.filter((item) => {
      if (item.group !== activeGroup) return false;
      if (!kw) return true;
      return (
        item.title.toLowerCase().includes(kw) ||
        item.desc.toLowerCase().includes(kw) ||
        item.meta.toLowerCase().includes(kw) ||
        item.tags.some((tag) => tag.toLowerCase().includes(kw))
      );
    });
  }, [activeGroup, keyword]);

  const currentGroup = resourceGroups.find((g) => g.key === activeGroup) ?? resourceGroups[0];

  return (
    <section id="resources" className="w-full bg-background-50 px-4 py-16 md:px-8 md:py-24">
      <div className="mx-auto w-full max-w-6xl">
        <SectionHeading
          eyebrow="资源导航"
          title="按版本切换，逐条找到你要的那一份"
          desc="剧集、小说、漫画分别整理。切换分类后还可以用关键词筛选，只留下你关心的条目。"
        />

        <div className="mt-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div
            role="tablist"
            aria-label="资源分类"
            className="inline-flex w-full gap-1 rounded-full border border-background-200 bg-background-100 p-1 sm:w-auto"
          >
            {resourceGroups.map((group) => {
              const active = group.key === activeGroup;
              return (
                <button
                  key={group.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setActiveGroup(group.key)}
                  className={`flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-2 font-label text-sm transition-colors sm:flex-none ${
                    active
                      ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                      : "text-foreground-700 hover:bg-background-200"
                  }`}
                >
                  <i className={`${group.icon} text-base`}></i>
                  {group.label}
                </button>
              );
            })}
          </div>

          <div className="relative w-full lg:w-72">
            <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-500"></i>
            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="筛选当前分类的条目…"
              aria-label="筛选资源条目"
              className="w-full rounded-md border border-background-200 bg-background-50 py-2.5 pl-9 pr-9 font-label text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
            />
            {keyword ? (
              <button
                type="button"
                aria-label="清空筛选"
                onClick={() => setKeyword("")}
                className="absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-md text-foreground-500 hover:bg-background-100"
              >
                <i className="ri-close-line text-base"></i>
              </button>
            ) : null}
          </div>
        </div>

        <p className="mt-4 font-label text-xs text-foreground-500">
          当前分类：{currentGroup.label} · {currentGroup.hint} · 共 {filtered.length} 条
        </p>

        {filtered.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((item) => (
              <article
                key={item.id}
                className="flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="h-40 w-full overflow-hidden bg-background-200 md:h-44">
                  <img
                    src={item.cover}
                    alt={`${item.title}封面图`}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="flex flex-1 flex-col p-5">
                  <p className="font-label text-xs text-accent-700">{item.meta}</p>
                  <h3 className="mt-2 font-heading text-base font-bold leading-snug text-foreground-950">
                    {item.title}
                  </h3>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-foreground-700">
                    {item.desc}
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <li
                        key={tag}
                        className="rounded-full bg-secondary-100 px-2.5 py-1 font-label text-xs text-secondary-900"
                      >
                        {tag}
                      </li>
                    ))}
                  </ul>
                </div>
              </article>
            ))}
          </div>
        ) : (
          <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-search-eye-line text-2xl"></i>
            </span>
            <p className="mt-4 font-heading text-base font-semibold text-foreground-900">
              没有匹配「{keyword}」的条目
            </p>
            <p className="mt-2 text-sm text-foreground-600">
              试试更短的关键词，或直接切换上方分类浏览全部内容。
            </p>
            <button
              type="button"
              onClick={() => setKeyword("")}
              className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 dark:text-foreground-950"
            >
              清空筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}